/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion -- DIRECT-239681 */

import { useMemo, useRef, useState } from 'react';
import { defaultSerializeQueryArgs, skipToken } from '@reduxjs/toolkit/query/react';
import type { Draft } from '@reduxjs/toolkit';
import { stableSerialize, useIsomorphicLayoutEffect, values, AnyFn, Optional } from '@direct-frontend/stdlib';
import type { DataProvider } from '@adv-frontend/data-provider';
import type { Patch } from '../immer-types';

import type { AnyQueryDefinition, QueryEndpointMethodsAndHooks, UseQueryStateDefaultResult } from '../apiSSRModule';
import { isTagsEqual } from '../invalidationTagsUtils';
import type { QueryArgFrom, ResultTypeFrom, SkipToken, TagDescription } from '../rtk-query-types';

import { BaseRowData, RequestedChunks, UseEndpointDataProviderPaginator } from './types';
import { createStaticRowsGenerator } from './createStaticRowsGenerator';
import { createDynamicRowsGenerator } from './createDynamicRowsGenerator';

/**
 * Тип возвращаемых данных из endpoint-датапровайдера.
 */
export interface EndpointDataProvider<
    Definition extends AnyQueryDefinition = AnyQueryDefinition,
    RowData extends BaseRowData = BaseRowData,
> extends DataProvider<RowData> {
    /**
     * Выполняется ли загрузка данных для первого чанка.
     */
    isLoading: boolean;
    /**
     * Выполняется ли обновление данных для первого чанка.
     */
    isFetching: boolean;
    /**
     * Возвращает список закешированных в датапровайдере значений
     */
    getCachedValues: () => UseQueryStateDefaultResult<Definition>[];
    /**
     * Возвращает параметры запроса для строки.
     * @param rowIndex Номер строки.
     * @returns Параметры запроса или skipToken.
     */
    getRowQueryArg: (rowIndex: number) => QueryArgFrom<Definition> | SkipToken;
}

/**
 * Базовая конфигурация провайдера.
 */
type BaseEndpointDataProviderConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
> = {
    /** Стратегия получения параметров окна для запросов эндпоинта */
    usePaginatorFactory: () => UseEndpointDataProviderPaginator<Definition, PaginatorParams>;
    /** Функция получения параметров запроса эндпоинта */
    getQueryArg(
        paginatorParams: PaginatorParams,
        queryArg: QueryArg,
        cachedValues: UseQueryStateDefaultResult<Definition>[]
    ): QueryArgFrom<Definition> | SkipToken;
    /** Функция получения строки из ответа эндпоинта */
    getRowByIndex(chunkData: ResultTypeFrom<Definition>, rowIndexInChunk: number): Optional<RowData>;
    /**
     * Функция получения списка тегов, которые приводят к инвалидации строки.
     * Если передана, то провайдер переключается в режим построчной инвалидации данных. При построчной инвалидации,
     * в случае инвалидации чанка, в строке флаг `isFetching` будет равен `true` только если чанк был инвалидирован
     * из-за тега этой строки.
     */
    getRowInvalidatedBy?(row: RowData): TagDescription<string>[];
};

/**
 * Конфигурация провайдера для фиксированной генерации строк.
 */
type EndpointDataProviderWithTotalCountConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
> = BaseEndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg> & {
    /**
     * Функция получения общего количества строк из ответа эндпоинта
     */
    getTotalCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Конфигурация провайдера для динамической генерации строк.
 */
type EndpointDataProviderWithoutTotalCountConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
> = BaseEndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg> & {
    /**
     * Функция получения количества строк в чанке из ответа эндпоинта
     */
    getRowsCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Конфигурация провайдера.
 */
type EndpointDataProviderConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
> =
    | EndpointDataProviderWithTotalCountConfig<Definition, PaginatorParams, RowData, QueryArg>
    | EndpointDataProviderWithoutTotalCountConfig<Definition, PaginatorParams, RowData, QueryArg>;

/**
 * Thunk обновляющий данные чанка
 */
export type UpdateQueryData<EndpointName extends string, Definition extends AnyQueryDefinition> = (
    /** Название эндпоинта */
    name: EndpointName,
    /** queryArg соответствующие чанку */
    queryArg: QueryArgFrom<Definition>,
    /** Функция обновляющая данные сущности */
    updateRecipe: (data: Draft<ResultTypeFrom<Definition>>) => void
) => {
    /** Метод, возвращающий данные в исходное состояние */
    undo: () => void;
    /** Набор immer-патчей, которые произошли внутри `updateRecipe` */
    patches: Patch[];
    /** Набор обратных immer-патчей */
    inversePatches: Patch[];
};

/**
 * Параметры для хука, возвращаемого методом createDataProvider.
 */
type EndpointDataProviderOptions = {
    /**
     * Индекс первой строки.
     * Важно указывать строку, которая отрендерится на сервере.
     */
    initialRowIndex?: number;
};

/**
 * Тип, который описывает параметр `createDataProvider` для query-эндпоинта.
 */
export type EndpointCreateDataProvider<Definition extends AnyQueryDefinition> = {
    createDataProvider: <PaginatorParams extends object, RowData extends BaseRowData, QueryArg>(
        config: EndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg>
    ) => (
        ...[queryArg]: undefined extends QueryArg
            ? [queryArg?: QueryArg, options?: EndpointDataProviderOptions]
            : [queryArg: QueryArg, options?: EndpointDataProviderOptions]
    ) => EndpointDataProvider<Definition, RowData>;
};

/**
 * Выводит тип данных строки в endpoint-датапровайдере.
 */
export type InferEndpointDataProviderRowData<T extends AnyFn> =
    ReturnType<T> extends EndpointDataProvider<any, infer RowData> ? RowData : never;

function isDataProviderWithTotalCountConfig<Definition extends AnyQueryDefinition, RowData extends BaseRowData>(
    config: EndpointDataProviderConfig<Definition, any, RowData, any>
): config is EndpointDataProviderWithTotalCountConfig<Definition, any, RowData, any> {
    return 'getTotalCount' in config;
}

/**
 * Метод для генерации createDataProvider метода эндпойнта.
 */
export function buildCreateEndpointDataProvider<EndpointName extends string, Definition extends AnyQueryDefinition>(
    endpoint: {
        name: EndpointName;
        subscribe: QueryEndpointMethodsAndHooks<Definition>['subscribe'];
        useQuery: QueryEndpointMethodsAndHooks<Definition>['useQuery'];
        useQueryState: QueryEndpointMethodsAndHooks<Definition>['useQueryState'];
        useCachedValues: QueryEndpointMethodsAndHooks<Definition>['useCachedValues'];
        updateQueryData: QueryEndpointMethodsAndHooks<Definition>['updateQueryData'];
    },
    definition: Definition
): EndpointCreateDataProvider<Definition>['createDataProvider'] {
    function serializeEndpointQueryArg(endpointQueryArg: QueryArgFrom<Definition> | typeof skipToken): string {
        const serializeResult = definition.serializeQueryArgs?.({
            queryArgs: endpointQueryArg,
            endpointDefinition: definition,
            endpointName: endpoint.name,
        });

        // Если `serializeQueryArgs` возвращает строку, то используем ее как ключ
        if (typeof serializeResult === 'string') {
            return serializeResult;
        }

        // Иначе используем дефолтный rtk-query сериализатор
        return defaultSerializeQueryArgs({
            // Передаем результат `serializeQueryArgs` если функция определена,
            // либо все queryArgs если не определена
            queryArgs: serializeResult ?? endpointQueryArg,
            endpointDefinition: definition,
            endpointName: endpoint.name,
        });
    }

    // Создание датапровайдера, источником данных для которого является эндпойнт.
    return function createEndpointDataProvider(config) {
        const { usePaginatorFactory: createUsePaginator, getQueryArg, getRowByIndex, getRowInvalidatedBy } = config;
        const usePaginator = createUsePaginator();
        const useRowsGenerator = isDataProviderWithTotalCountConfig(config)
            ? createStaticRowsGenerator({
                  getTotalCount: config.getTotalCount,
              })
            : createDynamicRowsGenerator({ getRowsCount: config.getRowsCount });

        return function useDataProvider(...[queryArg, { initialRowIndex = 0 } = {}]) {
            // Стабилизируем значение queryArg,
            // чтобы делать новые запросы только если параметры запроса действительно изменилось.
            const stableQueryArgString = useMemo(() => stableSerialize(queryArg), [queryArg]);
            // eslint-disable-next-line react-hooks/exhaustive-deps -- Стабилизируем ссылку через сериализацию в JSON.
            const stableQueryArg = useMemo(() => queryArg, [stableQueryArgString]);

            const initialPaginatorParams = useMemo(() => ({ requestedChunks: [], cachedValues: [] }), []);
            const initialPaginator = usePaginator(initialPaginatorParams);
            // Параметры начального запроса.
            const initialEndpointQueryArg = useMemo(
                () => getQueryArg(initialPaginator.getChunkParams(initialRowIndex), stableQueryArg!, []),
                [initialPaginator, initialRowIndex, stableQueryArg]
            );

            // Запоминаем параметры запроса и начальный индекс для всех чанков.
            // Чанки могут загружаться не последовательно.
            // Это позволяет получить все запрошенные провайдером данные ниже.
            const [requestedChunks, setRequestedChunks] = useState<RequestedChunks<Definition>>(
                initialEndpointQueryArg === skipToken
                    ? []
                    : [
                          {
                              queryArg: initialEndpointQueryArg,
                              serializedQueryArg: serializeEndpointQueryArg(initialEndpointQueryArg),
                              startIndex: initialRowIndex,
                          },
                      ]
            );

            // Получаем все запрошенные провайдером данные, чтобы сформировать список всех строк в провайдере.
            // Список всех строк в провайдере (в том числе и не загруженных) нужен, например, для react-table.
            const cachedValues = endpoint.useCachedValues(...requestedChunks.map(({ queryArg }) => queryArg));
            const paginator = usePaginator({ requestedChunks, cachedValues });

            // При первом вызове хука запрашиваем первый чанк,
            // общее количество строк и все доступные для выделения id.
            const { isFetching, isLoading, error, data } = endpoint.useQuery(initialEndpointQueryArg, {
                selectFromResult: ({ data, error, invalidatedBy, isFetching, isLoading }) => {
                    return {
                        data,
                        // Если первый чанк инвалидируется и включена построчная инвалидация, то в `isFetching` всего
                        // провайдера всегда отдаем `false`, чтобы определять этот флаг внутри строк
                        isFetching: invalidatedBy && getRowInvalidatedBy ? false : isFetching,
                        isLoading,
                        error,
                    };
                },
            });

            const rowsGenerator = useRowsGenerator(paginator, {
                getRowByIndex,
                requestedChunks,
                cachedValues,
                initialData: data,
            });

            // В случае ошибки запроса первого чанка выбрасываем её наружу,
            // чтобы поймать в ErrorBoundary.
            if (error) {
                throw error;
            }

            // Провайдер сам управляет подписками на данные, чтобы делать это более эффективно.
            // Создается одна подписка на чанк, вместо подписки для каждой строки.
            // Поэтому храним актуальный список подписок самостоятельно.
            const subscriptionsRef = useRef<Record<string, Function>>({});

            const rows = useMemo(() => rowsGenerator.getRows(), [rowsGenerator]);
            const loadedRows = useMemo(
                () =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- разные дженерики для типа RowData
                    rowsGenerator.getLoadedRows() as InferEndpointDataProviderRowData<
                        EndpointCreateDataProvider<Definition>['createDataProvider']
                    >,
                [rowsGenerator]
            );
            const totalCount = rowsGenerator.getTotalCount();

            // При смене параметров запроса или анмаунте провайдера – очищаем подписки и
            // список использованных параметров. Подписки очищаются в dataProvider,
            // а не в самих строках, так как строки могут быть виртуализированными и
            // не замаунченными в данный момент.
            useIsomorphicLayoutEffect(() => {
                return () => {
                    for (const unsubscribe of values(subscriptionsRef.current)) {
                        unsubscribe();
                    }

                    subscriptionsRef.current = {};
                    setRequestedChunks([]);
                };
            }, [stableQueryArg]);

            return useMemo(() => {
                return {
                    isFetching,
                    isLoading,
                    totalCount,
                    getRows: () => rows,
                    getLoadedRows: () => loadedRows,
                    getCachedValues: () => cachedValues,
                    getRowQueryArg: rowIndex => {
                        const chunkParams = paginator.getChunkParams(rowIndex);
                        return getQueryArg(chunkParams, stableQueryArg!, cachedValues);
                    },
                    updateRowDataManual: (
                        rowIndex: number,
                        updateRecipe: (rowData: NonNullable<ReturnType<typeof getRowByIndex>>) => void
                    ) => {
                        const chunkParams = paginator.getChunkParams(rowIndex);
                        const endpointQueryArg = getQueryArg(chunkParams, stableQueryArg!, cachedValues);

                        const { undo } = endpoint.updateQueryData(
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                            endpointQueryArg as QueryArgFrom<Definition>,
                            chunkData => {
                                const rowIndexInChunk = paginator.getRowIndexInChunk(rowIndex, chunkParams);
                                const data = chunkData ? getRowByIndex(chunkData, rowIndexInChunk) : undefined;

                                if (data) {
                                    updateRecipe(data);
                                }
                            }
                        );

                        return { undo };
                    },
                    useRowData(indexes: number | number[]) {
                        const rowIndex = Array.isArray(indexes) ? (indexes[0] ?? 0) : indexes;
                        const chunkParams = useMemo(
                            () => paginator.getChunkParams(rowIndex),
                            // Параметр `paginator` необходим для корректной работы хука
                            // eslint-disable-next-line react-hooks/exhaustive-deps -- см. выше
                            [rowIndex, paginator]
                        );

                        const endpointQueryArg = useMemo(
                            () => getQueryArg(chunkParams, stableQueryArg!, cachedValues),
                            // Параметр `stableQueryArg` необходим для корректной работы хука
                            // eslint-disable-next-line react-hooks/exhaustive-deps -- см. выше
                            [chunkParams, stableQueryArg]
                        );

                        const serializedQueryArg = useMemo(
                            () => serializeEndpointQueryArg(endpointQueryArg),
                            [endpointQueryArg]
                        );

                        const rowIndexInChunk = useMemo(
                            () => paginator.getRowIndexInChunk(rowIndex, chunkParams),
                            [rowIndex, chunkParams]
                        );

                        // 1. Вызываем запрос и создаем подписку, если её еще нет.
                        // 2. Запоминаем параметры запроса, чтобы позже провайдер мог сформировать полный список строк.
                        // Используем useLayoutEffect, чтобы все обновления стейта привели к синхронному ререндеру.
                        useIsomorphicLayoutEffect(() => {
                            const shouldSubscribe =
                                !subscriptionsRef.current[serializedQueryArg] && endpointQueryArg !== skipToken;

                            if (shouldSubscribe) {
                                subscriptionsRef.current[serializedQueryArg] = endpoint.subscribe(endpointQueryArg);

                                const chunkStartIndex = rowIndex - rowIndexInChunk;
                                const hasRequestedChunk = requestedChunks.some(
                                    chunk => chunk.serializedQueryArg === serializedQueryArg
                                );

                                if (!hasRequestedChunk) {
                                    setRequestedChunks(state => [
                                        ...state,
                                        {
                                            queryArg: endpointQueryArg,
                                            serializedQueryArg,
                                            startIndex: chunkStartIndex,
                                        },
                                    ]);
                                }
                            }
                        }, [rowIndex, rowIndexInChunk, endpointQueryArg, serializedQueryArg]);

                        return endpoint.useQueryState(endpointQueryArg, {
                            selectFromResult({
                                data: chunkData,
                                error,
                                invalidatedBy: chunkInvalidatedBy,
                                isError,
                                isFetching,
                                isLoading,
                                isUninitialized,
                            }) {
                                // В случае ошибки запроса выбрасываем её наружу, чтобы поймать в ErrorBoundary.
                                if (error) {
                                    throw error;
                                }

                                const willStartLoadingInEffect =
                                    isUninitialized && !subscriptionsRef.current[serializedQueryArg];
                                const data = chunkData ? getRowByIndex(chunkData, rowIndexInChunk) : undefined;

                                function isRowFetching() {
                                    // Если чанк инвалидируется и включена построчная инвалидация
                                    if (chunkInvalidatedBy && getRowInvalidatedBy) {
                                        const rowInvalidatedBy = data ? getRowInvalidatedBy(data) : [];

                                        // То в `isFetching = true`, если среди тегов чанка есть хотя бы один тег строки
                                        return chunkInvalidatedBy.some(chunkTag =>
                                            rowInvalidatedBy.some(rowTag => isTagsEqual(chunkTag, rowTag))
                                        );
                                    }

                                    return isFetching;
                                }

                                return {
                                    data,
                                    isLoading: isLoading || willStartLoadingInEffect,
                                    isFetching: isRowFetching(),
                                    isError,
                                };
                            },
                        });
                    },
                };
            }, [
                isFetching,
                isLoading,
                totalCount,
                rows,
                cachedValues,
                paginator,
                stableQueryArg,
                loadedRows,
                requestedChunks,
            ]);
        };
    };
}
